/** The app root: picks the panel for the current phase (UX-SPEC §4.1). */
import type { ComponentChildren } from 'preact';
import { useEffect } from 'preact/hooks';
import { bootJobs, bootKey } from './bench/controller.ts';
import { Intake } from './components/Intake.tsx';
import {
	CancelledNotice,
	ErrorPanel,
	InterruptedPanel,
	ProgressPanel,
} from './components/Panels.tsx';
import { Shell } from './components/Shell.tsx';
import { boot } from './controller.ts';
import { hashFor, route } from './router.ts';
import { notices, phase, phaseLog, result } from './state.ts';
import { APP_LABEL } from './strings-explain.ts';
import { HELP } from './strings-help.ts';
import { APP } from './strings.ts';
import { Notice } from './ui.tsx';
import { APP_VERSION } from './version.ts';
import { Glossary } from './views/Glossary.tsx';

function Landing({ children }: { children?: ComponentChildren }) {
	return (
		<main id="main" class="landing" tabIndex={-1}>
			<a class="skip" href="#main">
				{APP.skip}
			</a>
			<div class="landing-head">
				<div class="brand">{APP.name}</div>
				<p class="dim">{APP.tagline}</p>
				<p class="dim" data-testid="app-version">
					{APP_LABEL(APP_VERSION)}
				</p>
				<nav class="landing-help" aria-label={HELP.landingLinks}>
					<a href={HELP.userGuideHref} target="_blank" rel="noreferrer noopener">
						{HELP.userGuide}
					</a>
					<a href={HELP.limitationsHref} target="_blank" rel="noreferrer noopener">
						{HELP.limitations}
					</a>
					<a href={hashFor('glossary')}>{HELP.glossary}</a>
				</nav>
			</div>
			{notices.value.map((n) => (
				<Notice kind="info" key={n}>
					{n}
				</Notice>
			))}
			{children}
		</main>
	);
}

export function App() {
	useEffect(() => {
		void boot();
		bootKey();
		void bootJobs();
	}, []);
	const p = phase.value;
	const vm = result.value;
	let body: ComponentChildren;
	// the Glossary also reads without a result (from the landing links); not while an analysis is running
	const idle =
		p === 'empty' || p === 'cancelled' || p === 'error' || p === 'over-limit' || p === 'crashed';
	if (idle && !vm && route.value.view === 'glossary') {
		return (
			<div class="provenance" data-state={p} data-phases={phaseLog.value.join(' ')}>
				<Landing>
					<a class="btn-text" href="#/">
						{HELP.backToIntake}
					</a>
					<Glossary />
				</Landing>
			</div>
		);
	}
	switch (p) {
		case 'ready':
			body = vm ? <Shell vm={vm} /> : null;
			break;
		case 'reading':
		case 'fetching':
		case 'parsing':
		case 'intake-checked':
		case 'analysing':
		case 'rendering':
		case 'restoring':
			body = (
				<Landing>
					<ProgressPanel />
				</Landing>
			);
			break;
		case 'interrupted':
			body = (
				<Landing>
					<InterruptedPanel />
				</Landing>
			);
			break;
		case 'cancelled':
			body = (
				<Landing>
					<CancelledNotice />
					<Intake />
				</Landing>
			);
			break;
		case 'error':
		case 'over-limit':
		case 'crashed':
			body = (
				<Landing>
					<ErrorPanel />
					<Intake />
				</Landing>
			);
			break;
		default:
			body = (
				<Landing>
					<Intake />
				</Landing>
			);
	}
	return (
		<div class="provenance" data-state={p} data-phases={phaseLog.value.join(' ')}>
			{body}
		</div>
	);
}
